/* Magnetix - poster 3D viewer (theme asset: mgx-poster-3d.js). Same engine as the MagnetixTiles poster-3d.js.
 *
 * MTPoster3D.create(container, opts) -> Promise<viewer | null>   (null = no WebGL, use flat fallback)
 *   opts: { backPortrait, backLandscape, finish: 'gloss'|'matte', orientation: 'portrait'|'landscape' }
 *   opts.panels (optional): { cols, rows, ratio (whole picture w/h), gap (space between parts, share of the width) }
 *     draws a split picture ("תמונת חיבורים"): one photo cut into cols x rows separate plates. Without it: one poster.
 * viewer.setImage(canvasOrImg | null)   null = blank silver plate. Pass the same canvas again to refresh a live crop.
 * viewer.setFinish('gloss'|'matte')     animated material change
 * viewer.setOrientation('portrait'|'landscape')
 * viewer.showSide('front'|'back')
 * viewer.tune({ gloss:{...}, matte:{...}, exposure, key })   live look tuning
 * viewer.destroy()
 */
(function () {
  'use strict';

  var THREE_URL = 'https://cdn.jsdelivr.net/npm/three@0.169.0/build/three.module.min.js';
  var threePromise = null;
  // Loaded through an injected module script: Shopify minifies theme JS and turns a literal
  // dynamic import into require(), which does not exist in browsers.
  function loadThree() {
    if (threePromise) return threePromise;
    threePromise = new Promise(function (resolve, reject) {
      var cb = '__mtThree' + Date.now();
      var timer = setTimeout(function () { delete window[cb]; reject(new Error('three.js load timeout')); }, 20000);
      window[cb] = function (T) { clearTimeout(timer); delete window[cb]; resolve(T); };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'import * as T from "' + THREE_URL + '"; window["' + cb + '"](T);';
      document.head.appendChild(s);
    });
    threePromise.catch(function () { threePromise = null; });
    return threePromise;
  }

  function hasWebGL() {
    try {
      var c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl2') || c.getContext('webgl')));
    } catch (e) { return false; }
  }

  // Look presets. Gloss = sharp clear-coat streak like glass; Matte = one soft, faint glow.
  var DEFAULTS = {
    gloss: { roughness: 0.04, clearcoat: 0.8, clearcoatRoughness: 0.04, specularIntensity: 1, envMapIntensity: 1 }, // mirror-sharp, two layers (Frameology-style)
    matte: { roughness: 0.38, clearcoat: 0.35, clearcoatRoughness: 0.42, specularIntensity: 1, envMapIntensity: 1.3 }, // soft, blurred glow only
    exposure: 1,
    glow: 0.82,  // print shows its true colors (self-lit) ...
    shade: 0.22, // ... plus a little real room light so it still shades as it turns
    key: 2,
    envSpin: -0.15, // a soft light strip sits on the right third of the plate at rest
    hdrBoost: 4,
    envTiles: 4,
    envSquash: 0.45,
    envSheen: 0.9,
    envBands: 20,
    envBandWidth: 0.23,
    room: 'b3'
  };

  // Room presets for the reflection source
  var ROOMS = {
    frameology: { envTiles: 1, envSquash: 1, envSheen: 0, hdrBoost: 0, envSpin: 0 }, // their room, unmodified
    tuned: { envTiles: 4, envSquash: 0.45, envSheen: 0.9, envBands: 20, envBandWidth: 0.23, hdrBoost: 4, envSpin: -0.15 },
    b1: { envTiles: 4, envSquash: 0.45, envSheen: 0, hdrBoost: 4, envSpin: -0.75 },  // subtle: window edge on the right
    b2: { envTiles: 4, envSquash: 0.45, envSheen: 0, hdrBoost: 6, envSpin: -0.9 },   // bold: brighter window through the middle
    b3: { envTiles: 4, envSquash: 0.45, envSheen: 0.6, envBands: 4, envBandWidth: 0.5, hdrBoost: 4, envSpin: -0.78 }, // wide soft daylight, no lines
    studio: { hdrBoost: 4, envSpin: 0.3 }
  };

  var REST_Y = -0.2;   // resting turn so the plate reads as 3D
  var REST_X = 0.05;   // slight downward tilt
  var THICK = 0.026;   // 1mm plate, slightly exaggerated so the edge reads
  var BEVEL = 0.005;
  var RADIUS = 0.045;  // rounded corners

  function clone(o) { return JSON.parse(JSON.stringify(o)); }

  function roundedRect(THREE, w, h, r) {
    var s = new THREE.Shape(), x = -w / 2, y = -h / 2;
    s.moveTo(x + r, y);
    s.lineTo(x + w - r, y);
    s.absarc(x + w - r, y + r, r, -Math.PI / 2, 0, false);
    s.lineTo(x + w, y + h - r);
    s.absarc(x + w - r, y + h - r, r, 0, Math.PI / 2, false);
    s.lineTo(x + r, y + h);
    s.absarc(x + r, y + h - r, r, Math.PI / 2, Math.PI, false);
    s.lineTo(x, y + r);
    s.absarc(x + r, y + r, r, Math.PI, Math.PI * 1.5, false);
    return s;
  }

  // uvr (optional): the part of the photo this face shows, { u0, v0, u1, v1 }
  function faceGeometry(THREE, shape, w, h, uvr) {
    var g = new THREE.ShapeGeometry(shape, 12);
    var p = g.attributes.position, uv = g.attributes.uv;
    var r = uvr || { u0: 0, v0: 0, u1: 1, v1: 1 };
    for (var i = 0; i < p.count; i++) {
      uv.setXY(i, r.u0 + (p.getX(i) + w / 2) / w * (r.u1 - r.u0), r.v0 + (p.getY(i) + h / 2) / h * (r.v1 - r.v0));
    }
    uv.needsUpdate = true;
    return g;
  }


  function shadowTexture(THREE) {
    var c = document.createElement('canvas');
    c.width = c.height = 256;
    var x = c.getContext('2d');
    var g = x.createRadialGradient(128, 128, 0, 128, 128, 128);
    g.addColorStop(0, 'rgba(0,0,0,0.55)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    x.fillStyle = g;
    x.fillRect(0, 0, 256, 256);
    return new THREE.CanvasTexture(c);
  }

  // Living room built in code, baked to a PMREM env map. Env reflections depend on direction only,
  // so the room is deep (front wall 20 units away) to keep the window a small part of the plate.
  // At rest the plate shows roughly x -11.5..-5.8, y -4..4 of the front wall (z = 20).
  function buildEnv(THREE, renderer, key) {
    var scene = new THREE.Scene();
    function mat(r, g, b) { return new THREE.MeshBasicMaterial({ color: new THREE.Color(r, g, b), side: THREE.DoubleSide }); }
    function rect(w, h, m, x, y, z, ry) {
      var o = new THREE.Mesh(new THREE.PlaneGeometry(w, h), m);
      o.position.set(x, y, z); o.rotation.y = ry || 0; scene.add(o); return o;
    }
    function block(w, h, d, m, x, y, z) {
      var o = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), m);
      o.position.set(x, y, z); scene.add(o); return o;
    }
    function windowAt(cx, cy, z, w, h, ry, glow) {
      var frame = mat(0.75, 0.74, 0.72), t = 0.16;
      rect(w, h, glow, cx, cy, z, ry);
      var f = z > 0 ? -0.03 : 0.03, side = ry === Math.PI / 2;
      function bar(bw, bh, dx, dy) {
        if (side) rect(bw, bh, frame, cx + 0.03, cy + dy, z + dx, ry);
        else rect(bw, bh, frame, cx + dx, cy + dy, z + f, ry);
      }
      bar(w + t, t, 0, h / 2); bar(w + t, t, 0, -h / 2);
      bar(t, h + t, -w / 2, 0); bar(t, h + t, w / 2, 0);
      bar(t * 0.6, h, 0, 0); bar(w, t * 0.6, 0, h * 0.12);
    }
    var wall = mat(0.6, 0.55, 0.48), ceil = mat(0.72, 0.7, 0.66), floor = mat(0.26, 0.16, 0.09);
    // Room shell: x -16..16, y -4..6, z -12..20. Box face order: +x, -x, +y, -y, +z, -z
    var room = new THREE.Mesh(new THREE.BoxGeometry(32, 10, 32), [wall, wall, ceil, floor, wall, wall]);
    room.material.forEach(function (m) { m.side = THREE.BackSide; });
    room.position.set(0, 1, 4);
    scene.add(room);

    var sky = mat(key * 0.93, key, key * 1.06);
    // Front wall (behind the viewer): two windows, curtain, sofa, art, lamp, plant
    windowAt(-8.6, 1.7, 19.97, 3.3, 4.5, Math.PI, sky);
    windowAt(-3.2, 1.7, 19.97, 3.3, 4.5, Math.PI, sky);
    rect(1.3, 7, mat(0.95, 0.92, 0.86), -11.2, 1.4, 19.9, Math.PI);
    var fabric = mat(0.14, 0.13, 0.12);
    block(6, 1.2, 1.8, fabric, -7.6, -3.4, 19);
    block(6, 1.4, 0.5, fabric, -7.6, -2.3, 19.7);
    block(0.5, 0.9, 1.8, fabric, -10.4, -2.4, 19);
    block(0.5, 0.9, 1.8, fabric, -4.8, -2.4, 19);
    rect(2.4, 1.6, mat(0.05, 0.05, 0.05), 3.5, 1.2, 19.96, Math.PI);
    rect(2.1, 1.3, mat(0.55, 0.42, 0.32), 3.5, 1.2, 19.94, Math.PI);
    block(0.08, 4.6, 0.08, mat(0.05, 0.05, 0.05), -13, -1.7, 18.6);
    block(1, 0.8, 1, mat(3.2, 2.5, 1.6), -13, 0.9, 18.6);
    block(1, 1, 1, mat(0.5, 0.45, 0.4), -1.2, -3.5, 19);
    var leaves = new THREE.Mesh(new THREE.SphereGeometry(1.2, 16, 12), mat(0.05, 0.12, 0.04));
    leaves.position.set(-1.2, -1.9, 19); scene.add(leaves);
    // Side walls
    windowAt(-15.97, 1.5, 6, 3, 3, Math.PI / 2, mat(key * 0.6, key * 0.64, key * 0.68));
    var wood = mat(0.2, 0.13, 0.08);
    rect(5, 0.15, wood, 15.97, 0.5, 6, -Math.PI / 2);
    rect(5, 0.15, wood, 15.97, 2.2, 6, -Math.PI / 2);
    rect(0.9, 1.1, mat(0.35, 0.1, 0.08), 15.95, 1.1, 4.8, -Math.PI / 2);
    rect(1.3, 0.8, mat(0.1, 0.18, 0.3), 15.95, 2.65, 7.2, -Math.PI / 2);
    // Ceiling light and rug
    var bulb = new THREE.Mesh(new THREE.CircleGeometry(0.7, 24), mat(4, 3.6, 3));
    bulb.position.set(0, 5.97, 6); bulb.rotation.x = Math.PI / 2; scene.add(bulb);
    var rug = rect(10, 6, mat(0.45, 0.38, 0.32), -2, -3.98, 10, 0);
    rug.rotation.x = -Math.PI / 2;

    var pmrem = new THREE.PMREMGenerator(renderer);
    var rt = pmrem.fromScene(scene, 0, 0.05, 80);
    pmrem.dispose();
    scene.traverse(function (o) {
      if (o.geometry) o.geometry.dispose();
      if (o.material) [].concat(o.material).forEach(function (m) { m.dispose(); });
    });
    return rt;
  }

  function create(container, opts) {
    opts = opts || {};
    if (!hasWebGL()) return Promise.resolve(null);
    return loadThree().then(function (THREE) { return build(THREE, container, opts); }, function () { return null; });
  }

  function build(THREE, container, opts) {
    var look = clone(DEFAULTS);
    if (opts.room && ROOMS[opts.room]) look.room = opts.room;
    Object.assign(look, ROOMS[look.room]);
    var finish = opts.finish === 'matte' ? 'matte' : 'gloss';
    var orientation = opts.orientation === 'landscape' ? 'landscape' : 'portrait';
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NeutralToneMapping; // keeps photo colors true
    renderer.toneMappingExposure = look.exposure;
    renderer.setClearColor(0x000000, 0);
    var canvas = renderer.domElement;
    canvas.style.cssText = 'display:block;width:100%;height:100%;touch-action:pan-y;cursor:grab;outline:none';
    container.appendChild(canvas);

    var scene = new THREE.Scene();
    var envRT = buildEnv(THREE, renderer, look.key);
    scene.environment = envRT.texture;
    scene.environmentRotation.y = look.envSpin;
    scene.add(new THREE.AmbientLight(0xffffff, 0.35));

    var camera = new THREE.PerspectiveCamera(28, 1, 0.1, 100);
    var maxAniso = renderer.capabilities.getMaxAnisotropy();
    var loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');

    // Optional studio photo (equirectangular JPG) as the reflection source; the code-built room is the fallback.
    // A JPG caps windows at plain white, so bright areas are expanded back toward real HDR levels
    // (windows ~10x brighter than walls). That is what makes glass reflections read.
    var envImageRT = null, envPixels = null;
    function bakeEnvImage() {
      var w = envPixels.w, h = envPixels.h, src = envPixels.data, n = w * h;
      var out = new Uint16Array(n * 4), k = look.hdrBoost, lut = new Uint16Array(256), one = THREE.DataUtils.toHalfFloat(1);
      for (var j = 0; j < 256; j++) {
        var s = j / 255, lin = s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
        lut[j] = THREE.DataUtils.toHalfFloat(lin * (1 + k * Math.pow(lin, 4)));
      }
      for (var i = 0; i < n; i++) {
        var o = ((h - 1 - Math.floor(i / w)) * w + (i % w)) * 4; // canvas rows run top-down, texture rows bottom-up
        for (var c = 0; c < 3; c++) {
          out[o + c] = lut[src[i * 4 + c]];
        }
        out[o + 3] = one;
      }
      var tex = new THREE.DataTexture(out, w, h, THREE.RGBAFormat, THREE.HalfFloatType);
      tex.mapping = THREE.EquirectangularReflectionMapping;
      tex.colorSpace = THREE.LinearSRGBColorSpace;
      tex.magFilter = tex.minFilter = THREE.LinearFilter;
      tex.flipY = false;
      tex.needsUpdate = true;
      var pm = new THREE.PMREMGenerator(renderer);
      if (envImageRT) envImageRT.dispose();
      envImageRT = pm.fromEquirectangular(tex);
      pm.dispose(); tex.dispose();
      scene.environment = envImageRT.texture;
      requestRender();
    }
    if (opts.envImage) {
      var envImg = new Image();
      envImg.crossOrigin = 'anonymous';
      envImg.onload = function () { sampleEnvImage(); bakeEnvImage(); };
      envImg.src = opts.envImage;
    }
    // Product-photo studio painted as a 360 panorama: dark gray room with large softboxes.
    function drawStudio(x, w, h) {
      var bg = x.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, '#7d7975'); bg.addColorStop(0.5, '#4d4a47'); bg.addColorStop(1, '#1f1d1c');
      x.fillStyle = bg; x.fillRect(0, 0, w, h);
      x.filter = 'blur(' + Math.round(w / 300) + 'px)';
      function box(u, v, bw, bh, col) { x.fillStyle = col; x.fillRect((u - bw / 2) * w, (v - bh / 2) * h, bw * w, bh * h); }
      box(0.75, 0.47, 0.022, 0.5, '#ffffff');   // tall strip softbox: the main glass streak
      box(0.83, 0.45, 0.07, 0.26, '#f4efe8');   // large square softbox, warm
      box(0.64, 0.5, 0.012, 0.36, '#d9d9d9');   // thin kicker strip
      box(0.5, 0.08, 0.5, 0.06, '#e8e8e8');     // overhead light
      box(0.25, 0.45, 0.06, 0.3, '#eeeeee');    // softbox behind, for the back and edges
      box(0.05, 0.5, 0.015, 0.4, '#cfcfcf');
      x.filter = 'none';
    }
    // Shrinks the room in the reflection: the panorama is repeated envTiles times around the circle
    // and its middle band squashed vertically, so objects read smaller and something always passes the plate.
    function sampleEnvImage() {
      var w = 1536, h = 768, c = document.createElement('canvas');
      c.width = w; c.height = h;
      var x = c.getContext('2d'), n = Math.max(1, Math.round(look.envTiles)), tw = w / n;
      var bh = h * look.envSquash, by = (h - bh) / 2;
      if (look.room === 'studio') { drawStudio(x, w, h); envPixels = { w: w, h: h, data: x.getImageData(0, 0, w, h).data }; return; }
      for (var i = 0; i < n; i++) {
        x.drawImage(envImg, i * tw, 0, tw, h);       // full room: ceiling and floor
        x.drawImage(envImg, i * tw, by, tw, bh);     // squashed walls, windows and furniture
      }
      // Soft daylight bands between the windows, so the gloss always shows a moving sheen.
      if (look.envSheen > 0) {
        var sc = document.createElement('canvas');
        sc.width = w; sc.height = h;
        var sx = sc.getContext('2d'), bands = look.envBands, p = w / bands;
        for (var b = 0; b < bands; b++) {
          var g = sx.createLinearGradient(b * p, 0, (b + 1) * p, 0);
          g.addColorStop(Math.max(0, 0.5 - look.envBandWidth), 'rgba(255,255,255,0)');
          g.addColorStop(0.5, 'rgba(255,252,246,' + look.envSheen + ')');
          g.addColorStop(Math.min(1, 0.5 + look.envBandWidth), 'rgba(255,255,255,0)');
          sx.fillStyle = g;
          sx.fillRect(b * p, 0, p, h);
        }
        var vm = sx.createLinearGradient(0, by, 0, by + bh);
        vm.addColorStop(0, 'rgba(0,0,0,0)');
        vm.addColorStop(0.35, 'rgba(0,0,0,1)');
        vm.addColorStop(0.75, 'rgba(0,0,0,1)');
        vm.addColorStop(1, 'rgba(0,0,0,0)');
        sx.globalCompositeOperation = 'destination-in';
        sx.fillStyle = vm;
        sx.fillRect(0, 0, w, h);
        x.globalCompositeOperation = 'screen';
        x.drawImage(sc, 0, 0);
        x.globalCompositeOperation = 'source-over';
      }
      envPixels = { w: w, h: h, data: x.getImageData(0, 0, w, h).data };
    }

    // Materials
    var plain = null; // blank plate = smooth silver, no texture
    var frontMat = new THREE.MeshPhysicalMaterial({ color: 0xc9ccd0, metalness: 1, roughness: 0.22 });
    var edgeMat = new THREE.MeshPhysicalMaterial({ color: 0xdadde1, metalness: 0.8, roughness: 0.2 });
    var backMat = new THREE.MeshPhysicalMaterial({ color: 0xffffff, roughness: 0.32, metalness: 0.55 }); // printed aluminum: satin metallic sheen
    var hiddenMat = new THREE.MeshBasicMaterial({ visible: false });
    var shadowMat = new THREE.MeshBasicMaterial({ map: shadowTexture(THREE), transparent: true, depthWrite: false, opacity: 0.5 });

    var backTex = {};
    function backFor(o) {
      var url = o === 'landscape' ? opts.backLandscape : opts.backPortrait;
      if (!url) return null;
      if (!backTex[o]) {
        backTex[o] = loader.load(url, function () { requestRender(); });
        backTex[o].colorSpace = THREE.SRGBColorSpace;
        backTex[o].anisotropy = maxAniso;
      }
      return backTex[o];
    }

    var plate = new THREE.Group();
    scene.add(plate);
    var shadow = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), shadowMat);
    shadow.rotation.x = -Math.PI / 2;
    scene.add(shadow);
    var meshes = [];
    var W = 2, H = 3;
    var panels = opts.panels && opts.panels.cols && opts.panels.rows ? opts.panels : null;

    // Split picture: the photo is cut into equal parts (no photo is lost in the gaps), each its own plate
    function buildPanels() {
      var ratio = panels.ratio || (panels.cols / panels.rows);
      if (ratio >= 1) { W = 3.4; H = W / ratio; } else { H = 3.4; W = H * ratio; }
      var gap = (panels.gap == null ? 0.012 : panels.gap) * W;
      var cols = panels.cols, rows = panels.rows;
      var pw = (W - gap * (cols - 1)) / cols, ph = (H - gap * (rows - 1)) / rows;
      var shape = roundedRect(THREE, pw, ph, RADIUS * 0.4);
      var face = THICK / 2 + BEVEL + 0.0006;
      backMat.map = null;
      backMat.color.set(0xd6d6d6);
      backMat.needsUpdate = true;
      for (var r = 0; r < rows; r++) {
        for (var c = 0; c < cols; c++) {
          var x = -W / 2 + pw / 2 + c * (pw + gap), y = H / 2 - ph / 2 - r * (ph + gap);
          var body = new THREE.ExtrudeGeometry(shape, { depth: THICK, bevelEnabled: true, bevelThickness: BEVEL, bevelSize: BEVEL, bevelSegments: 4, curveSegments: 12 });
          body.translate(x, y, -THICK / 2);
          var front = faceGeometry(THREE, shape, pw, ph, { u0: c / cols, u1: (c + 1) / cols, v0: 1 - (r + 1) / rows, v1: 1 - r / rows });
          front.translate(x, y, face);
          var back = faceGeometry(THREE, shape, pw, ph);
          back.rotateY(Math.PI);
          back.translate(x, y, -face);
          meshes.push(new THREE.Mesh(body, [hiddenMat, edgeMat]), new THREE.Mesh(front, frontMat), new THREE.Mesh(back, backMat));
        }
      }
      meshes.forEach(function (m) { plate.add(m); });
      shadow.scale.set(W * 1.3, 0.9, 1);
      shadow.position.set(0, -H / 2 - 0.22, 0);
      fit();
    }

    function buildPlate() {
      meshes.forEach(function (m) { plate.remove(m); m.geometry.dispose(); });
      meshes = [];
      if (panels) { buildPanels(); return; }
      W = orientation === 'landscape' ? 3 : 2;
      H = orientation === 'landscape' ? 2 : 3;
      var shape = roundedRect(THREE, W, H, RADIUS);
      var body = new THREE.ExtrudeGeometry(shape, { depth: THICK, bevelEnabled: true, bevelThickness: BEVEL, bevelSize: BEVEL, bevelSegments: 4, curveSegments: 12 });
      body.translate(0, 0, -THICK / 2);
      var face = THICK / 2 + BEVEL + 0.0006;
      var front = faceGeometry(THREE, shape, W, H);
      front.translate(0, 0, face);
      var back = faceGeometry(THREE, shape, W, H);
      back.rotateY(Math.PI);
      back.translate(0, 0, -face);
      backMat.map = backFor(orientation);
      backMat.color.set(backMat.map ? 0xffffff : 0xd6d6d6);
      backMat.needsUpdate = true;
      meshes = [new THREE.Mesh(body, [hiddenMat, edgeMat]), new THREE.Mesh(front, frontMat), new THREE.Mesh(back, backMat)];
      meshes.forEach(function (m) { plate.add(m); });
      shadow.scale.set(W * 1.3, 0.9, 1);
      shadow.position.set(0, -H / 2 - 0.22, 0);
      fit();
    }

    function fit() {
      var w = container.clientWidth || 1, h = container.clientHeight || 1;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      var t = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2));
      var margin = 1.28;
      var dist = Math.max((H / 2 * margin) / t, (W / 2 * margin) / (t * camera.aspect)) + THICK;
      camera.position.set(0, 0, dist);
      camera.lookAt(0, 0, 0);
      camera.updateProjectionMatrix();
      requestRender();
    }

    // Front image
    var photoTex = null, photoSrc = null;
    function setImage(src) {
      if (!src) {
        if (photoTex) photoTex.dispose();
        photoTex = photoSrc = null;
        frontMat.map = plain; frontMat.color.set(0xc9ccd0); frontMat.emissiveMap = null; frontMat.emissive.set(0x000000);
        frontMat.needsUpdate = true;
        requestRender();
        return;
      }
      var sw = src.width || src.naturalWidth, sh = src.height || src.naturalHeight;
      if (src === photoSrc && photoTex && photoTex.userData.w === sw && photoTex.userData.h === sh) {
        photoTex.needsUpdate = true;
        requestRender();
        return;
      }
      if (photoTex) photoTex.dispose();
      photoSrc = src;
      photoTex = src instanceof HTMLCanvasElement ? new THREE.CanvasTexture(src) : new THREE.Texture(src);
      photoTex.colorSpace = THREE.SRGBColorSpace;
      photoTex.anisotropy = maxAniso;
      photoTex.userData.w = sw; photoTex.userData.h = sh; // a size change needs a new texture
      photoTex.needsUpdate = true;
      frontMat.map = photoTex; frontMat.color.setScalar(look.shade); frontMat.emissiveMap = photoTex; frontMat.emissive.set(0xffffff);
      frontMat.needsUpdate = true;
      requestRender();
    }

    // Material animation toward the target finish
    function target() {
      var f = look[finish];
      var blank = !photoTex;
      return {
        roughness: blank ? 0.22 : f.roughness,
        metalness: blank ? 1 : 0,
        clearcoat: f.clearcoat,
        clearcoatRoughness: f.clearcoatRoughness,
        specularIntensity: f.specularIntensity,
        envMapIntensity: blank ? 1 : f.envMapIntensity,
        emissiveIntensity: blank ? 0 : look.glow
      };
    }
    function stepMaterial() {
      var t = target(), moving = false;
      for (var k in t) {
        var d = t[k] - frontMat[k];
        if (Math.abs(d) > 0.002) { frontMat[k] += d * 0.14; moving = true; } else frontMat[k] = t[k];
      }
      return moving;
    }

    // Rotation: drag spins 360 with momentum, idle eases home with a slow sway.
    var rotY = REST_Y, rotX = REST_X, vel = 0, dragging = false, lastX = 0, lastY = 0, lastTouch = 0, home = 0, pointerId = null;
    function nearestSide(a) { var n = Math.round((a - REST_Y) / Math.PI); return n * Math.PI; }
    canvas.addEventListener('pointerdown', function (e) {
      dragging = true; pointerId = e.pointerId; lastX = e.clientX; lastY = e.clientY; vel = 0;
      canvas.setPointerCapture(e.pointerId);
      canvas.style.cursor = 'grabbing';
      container.dispatchEvent(new CustomEvent('mtposter:interact'));
      requestRender();
    });
    canvas.addEventListener('pointermove', function (e) {
      if (!dragging || e.pointerId !== pointerId) return;
      var dx = e.clientX - lastX, dy = e.clientY - lastY;
      lastX = e.clientX; lastY = e.clientY;
      var s = 5.5 / Math.max(container.clientWidth, 200);
      rotY += dx * s;
      vel = dx * s;
      if (e.pointerType === 'mouse') rotX = Math.max(-0.3, Math.min(0.3, rotX + dy * s * 0.5));
      requestRender();
    });
    function endDrag(e) {
      if (!dragging || (e && e.pointerId !== pointerId)) return;
      dragging = false; lastTouch = performance.now();
      canvas.style.cursor = 'grab';
      home = nearestSide(rotY + vel * 12);
    }
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);
    canvas.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        vel = e.key === 'ArrowLeft' ? -0.06 : 0.06; lastTouch = performance.now(); home = nearestSide(rotY + vel * 12);
        requestRender();
        e.preventDefault();
      }
    });
    canvas.tabIndex = 0;
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', 'תצוגת תלת-ממד של פוסטר המתכת שלכם. גררו או השתמשו בחצים כדי לסובב.');

    // Render loop, only while visible
    var visible = true, raf = 0, clock = performance.now();
    function requestRender() { if (!raf && visible && !document.hidden) raf = requestAnimationFrame(frame); }
    function frame(now) {
      raf = 0;
      var dt = Math.min(now - clock, 50) / 16.7; clock = now;
      var animating = stepMaterial();
      if (!dragging) {
        if (Math.abs(vel) > 0.0008) {
          rotY += vel * dt; vel *= Math.pow(0.93, dt); animating = true;
        } else if (now - lastTouch > 1200) {
          // shift the target by whole turns so the plate never unwinds
          var base = home + REST_Y + Math.round((rotY - home - REST_Y) / (2 * Math.PI)) * 2 * Math.PI;
          var sway = reduced ? 0 : 0.035 * Math.sin(now / 1900);
          rotY += (base + sway - rotY) * 0.05 * dt;
          rotX += (REST_X - rotX) * 0.05 * dt;
          animating = animating || !reduced || Math.abs(base - rotY) > 0.001;
        } else animating = true;
      } else animating = true;
      plate.rotation.set(rotX, rotY, 0);
      renderer.render(scene, camera);
      if (animating) requestRender();
    }

    var io = 'IntersectionObserver' in window ? new IntersectionObserver(function (en) {
      visible = en[0].isIntersecting;
      if (visible) { clock = performance.now(); requestRender(); }
    }) : null;
    if (io) io.observe(container);
    var ro = 'ResizeObserver' in window ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(container); else window.addEventListener('resize', fit);
    function onVis() { if (!document.hidden) { clock = performance.now(); requestRender(); } }
    document.addEventListener('visibilitychange', onVis);

    buildPlate();
    if (opts.image) setImage(opts.image);
    stepMaterial();

    return {
      THREE: THREE,
      setImage: setImage,
      setFinish: function (f) { finish = f === 'matte' ? 'matte' : 'gloss'; requestRender(); },
      setOrientation: function (o) {
        o = o === 'landscape' ? 'landscape' : 'portrait';
        if (o === orientation || panels) return; // a split picture keeps its own layout
        orientation = o;
        buildPlate();
      },
      showSide: function (side) {
        home = side === 'back' ? Math.PI : 0;
        vel = 0; lastTouch = 0;
        requestRender();
      },
      setRoom: function (name) {
        if (!ROOMS[name]) return;
        Object.assign(look, ROOMS[name]);
        look.room = name;
        scene.environmentRotation.y = look.envSpin;
        if (name === 'studio' || (envImg && envImg.complete && envImg.naturalWidth)) { sampleEnvImage(); bakeEnvImage(); }
      },
      tune: function (t) {
        if (t.gloss) Object.assign(look.gloss, t.gloss);
        if (t.matte) Object.assign(look.matte, t.matte);
        if (t.exposure != null) { look.exposure = t.exposure; renderer.toneMappingExposure = t.exposure; }
        if (t.glow != null) look.glow = t.glow;
        if (t.hdrBoost != null && t.hdrBoost !== look.hdrBoost) { look.hdrBoost = t.hdrBoost; if (envPixels) bakeEnvImage(); }
        if ((t.envTiles != null && t.envTiles !== look.envTiles) || (t.envSquash != null && t.envSquash !== look.envSquash) || (t.envSheen != null && t.envSheen !== look.envSheen) || (t.envBands != null && t.envBands !== look.envBands) || (t.envBandWidth != null && t.envBandWidth !== look.envBandWidth)) {
          if (t.envTiles != null) look.envTiles = t.envTiles;
          if (t.envSquash != null) look.envSquash = t.envSquash;
          if (t.envSheen != null) look.envSheen = t.envSheen;
          if (t.envBands != null) look.envBands = t.envBands;
          if (t.envBandWidth != null) look.envBandWidth = t.envBandWidth;
          if (envPixels) { sampleEnvImage(); bakeEnvImage(); }
        }
        if (t.shade != null) { look.shade = t.shade; if (photoTex) frontMat.color.setScalar(t.shade); }
        if (t.envSpin != null) { look.envSpin = t.envSpin; scene.environmentRotation.y = t.envSpin; }
        if (t.key != null && t.key !== look.key && !envImageRT) {
          look.key = t.key;
          envRT.dispose();
          envRT = buildEnv(THREE, renderer, look.key);
          scene.environment = envRT.texture;
        }
        requestRender();
      },
      getLook: function () { return clone(look); },
      renderOnce: function () { for (var i = 0; i < 80 && stepMaterial(); i++); plate.rotation.set(rotX, rotY, 0); renderer.render(scene, camera); }, // for screenshots in background tabs
      destroy: function () {
        cancelAnimationFrame(raf);
        if (io) io.disconnect();
        if (ro) ro.disconnect(); else window.removeEventListener('resize', fit);
        document.removeEventListener('visibilitychange', onVis);
        renderer.dispose();
        envRT.dispose(); if (envImageRT) envImageRT.dispose();
        canvas.remove();
      }
    };
  }

  window.MTPoster3D = { create: create, defaults: DEFAULTS };
})();
